import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { postEgreso } from "../../../redux/actions";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import SuccessModal from "../../../components/modals/successModal";
import ErrorModal from "../../../components/modals/errorModal";
import Button from "../../../components/button";

function EgresarVehiculoComponent({
  onCloseEgresar,
  idLevantamiento,
  refreshDetalle,
}) {
  const dispatch = useDispatch();

  const { id } = useParams();

  const userId = useSelector((state) => state.currentUserId);

  const firmaRef = useRef(null);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState({
    show: false,
    message: "",
  });

  const openErrorModal = (message) =>
    setErrorModal({ show: true, message });

  const closeErrorModal = () =>
    setErrorModal({ show: false, message: "" });

  const onSubmit = async (data, event) => {
    event.preventDefault();

    if (firmaRef.current.isEmpty()) {
      setError("firma", {
        type: "manual",
        message: "Por favor, complete la firma",
      });
      return;
    }

    setLoading(true);

    try {
      // Agregar información extra
      data.userId = userId;
      data.idSecuestro = id;
      data.idLevantamiento = idLevantamiento;

      // Obtener la firma en Base64
      const firmaDataUrl = firmaRef.current.toDataURL("image/png");

      // Convertir Base64 a Blob
      const blob = await fetch(firmaDataUrl).then((res) => res.blob());

      const formData = new FormData();

      // Agregar los demás campos al formData
      for (const key in data) {
        if (key !== "firma") {
          formData.append(key, data[key]);
        }
      }

      // Agregar la firma como archivo binario
      formData.append("firma", blob, "firma.png");

      const response = await dispatch(postEgreso(formData));

      if (response) {
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Error al enviar el formulario:", error);

      const serverErrorMessage =
        error.response?.data?.error ||
        "Error inesperado al registrar el egreso del vehículo.";

      openErrorModal(serverErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);

    if (typeof refreshDetalle === "function") {
      refreshDetalle();
    }

    onCloseEgresar();
  };

  const limpiarFirma = () => {
    firmaRef.current.clear();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onCloseEgresar}
    >
      {showSuccessModal && (
        <SuccessModal
          closeSuccessModal={handleCloseSuccess}
          message="Vehículo egresado con éxito."
        />
      )}

      {errorModal.show && (
        <ErrorModal
          closeErrorModal={closeErrorModal}
          message={errorModal.message}
        />
      )}

      <div
        className="relative w-[20rem] sm:w-[30rem] px-5 py-8 bg-white rounded-xl flex flex-col justify-center gap-6 mx-5 overflow-y-auto my-[2rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCloseEgresar}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>

        <div className="sm:text-left text-center text-[#3d4245] text-2xl font-bold font-inter">
          Egresar vehículo
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Contenido principal */}
          <div className="self-stretch h-[414px] flex flex-col justify-start items-start gap-4">
            {/* Apellidos y Nombres / DNI */}
            <div className="self-stretch flex justify-start items-start gap-3 sm:flex-row flex-col">
              <div className="flex-grow flex flex-col justify-start items-start gap-2 w-full">
                <label className="text-[#3d4245] text-sm font-normal font-inter">
                  Apellidos y Nombres
                </label>

                <input
                  placeholder="Apellidos y Nombres"
                  className="self-stretch h-[50px] p-2 rounded-md border border-[#687073] text-sm font-normal font-inter w-full flex-grow"
                  {...register("nombreCompleto", {
                    required: "Por favor, ingrese el apellido y nombre",
                    validate: (value) => {
                      const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s\-'´]+$/;

                      if (!regex.test(value)) {
                        return "El nombre solo puede contener letras, espacios, apóstrofes y guiones.";
                      }

                      if (value.length < 2) {
                        return "El nombre debe tener al menos 2 caracteres.";
                      }

                      if (value.length > 50) {
                        return "El nombre no debe superar los 50 caracteres.";
                      }

                      if (/\s{2,}/.test(value)) {
                        return "El nombre no debe contener espacios consecutivos.";
                      }

                      return true;
                    },
                    setValueAs: (value) =>
                      value
                        ?.trim()
                        .toLowerCase()
                        .replace(/\b\w/g, (char) => char.toUpperCase()),
                  })}
                />
              </div>

              <div className="flex-grow flex flex-col justify-start items-start gap-2 w-full">
                <label className="text-[#3d4245] text-sm font-normal font-inter">
                  DNI
                </label>

                <input
                  placeholder="000000000"
                  className="self-stretch h-[50px] p-2 rounded-md border border-[#687073] text-sm font-normal font-inter w-full flex-grow"
                  type="number"
                  {...register("dni", {
                    required: "Por favor, ingrese el dni",
                    validate: (value) => {
                      if (!/^\d+$/.test(value)) {
                        return "El DNI solo puede contener números.";
                      }

                      if (value.length < 6 || value.length > 9) {
                        return "El DNI debe tener entre 6 y 9 dígitos.";
                      }

                      return true;
                    },
                    setValueAs: (value) => value?.trim(),
                  })}
                />
              </div>
            </div>

            {/* Domicilio / Lic. Conducir */}
            <div className="self-stretch flex justify-start items-start gap-3 sm:flex-row flex-col">
              <div className="flex-grow flex flex-col justify-start items-start gap-2 w-full">
                <label
                  htmlFor="address"
                  className="text-[#3d4245] text-sm font-normal font-inter"
                >
                  Domicilio
                </label>

                <input
                  placeholder="Domicilio"
                  className="self-stretch h-[50px] p-2 rounded-md border border-[#687073] text-sm font-normal font-inter w-full flex-grow"
                  type="text"
                  {...register("domicilio", {
                    validate: (value) => {
                      if (!value) {
                        return true;
                      }

                      if (!/^[a-zA-Z0-9\s.,º#/-]+$/.test(value)) {
                        return "El domicilio contiene caracteres no permitidos.";
                      }

                      if (value.length < 5) {
                        return "El domicilio debe tener al menos 5 caracteres.";
                      }

                      return true;
                    },
                    setValueAs: (value) => value?.trim(),
                  })}
                />
              </div>

              <div className="flex-grow flex flex-col justify-start items-start gap-2 w-full">
                <label
                  htmlFor="license"
                  className="text-[#3d4245] text-sm font-normal font-inter"
                >
                  Lic. Conducir
                </label>

                <input
                  placeholder="000000000"
                  className="self-stretch h-[50px] p-2 rounded-md border border-[#687073] text-sm font-normal font-inter w-full flex-grow"
                  type="number"
                  {...register("licencia", {
                    validate: (value) => {
                      if (!value) {
                        return true;
                      }

                      if (!/^\d+$/.test(value)) {
                        return "La licencia solo puede contener números.";
                      }

                      return true;
                    },
                    setValueAs: (value) => value?.trim(),
                  })}
                />
              </div>
            </div>

            {/* Tarj. Verde / B. Pago */}
            <div className="self-stretch flex justify-start items-start gap-3 sm:flex-row flex-col">
              <div className="flex-grow flex flex-col justify-start items-start gap-2 w-full">
                <label
                  htmlFor="green-card"
                  className="text-[#3d4245] text-sm font-normal font-inter"
                >
                  Tarj. Verde
                </label>

                <input
                  placeholder="Tarj. Verde"
                  className="self-stretch h-[50px] p-2 rounded-md border border-[#687073] text-sm font-normal font-inter w-full flex-grow uppercase"
                  type="text"
                  {...register("tarjetaVerde", {
                    validate: (value) => {
                      if (!value) {
                        return true;
                      }

                      if (!/^[a-zA-Z0-9\s.,º#/-]+$/.test(value)) {
                        return "La tarjeta verde contiene caracteres no permitidos.";
                      }

                      return true;
                    },
                    setValueAs: (value) => value?.trim().toUpperCase(),
                  })}
                />
              </div>

              <div className="flex-grow flex flex-col justify-start items-start gap-2 w-full">
                <label
                  htmlFor="payment-slip"
                  className="text-[#3d4245] text-sm font-normal font-inter"
                >
                  B. Pago
                </label>

                <input
                  placeholder="000000000"
                  className="self-stretch h-[50px] p-2 rounded-md border border-[#687073] text-sm font-normal font-inter w-full flex-grow"
                  type="number"
                  {...register("bPago", {
                    validate: (value) => {
                      if (!value) {
                        return true;
                      }

                      if (!/^\d+$/.test(value)) {
                        return "La boleta de pago solo puede contener números.";
                      }

                      return true;
                    },
                    setValueAs: (value) => value?.trim(),
                  })}
                />
              </div>
            </div>

            {/* Observaciones / Fecha y hora */}
            <div className="self-stretch flex justify-start items-start gap-3 sm:flex-row flex-col">
              <div className="flex-grow flex flex-col justify-start items-start gap-2 w-full">
                <label className="text-[#3d4245] text-sm font-normal font-inter">
                  Observaciones
                </label>

                <input
                  placeholder="Observaciones"
                  className="self-stretch h-[50px] p-2 rounded-md border border-[#687073] text-sm font-normal font-inter w-full flex-grow"
                  type="text"
                  {...register("observaciones", {
                    validate: (value) => {
                      if (!value) {
                        return true;
                      }

                      if (value.length > 500) {
                        return "Las observaciones no pueden superar los 500 caracteres.";
                      }

                      return true;
                    },
                    setValueAs: (value) => value?.trim(),
                  })}
                />
              </div>

              <div className="flex-grow flex flex-col justify-start items-start gap-2 w-full">
                <label className="text-[#3d4245] text-sm font-normal font-inter">
                  Fecha y hora de salida
                </label>

                <div className="flex items-center gap-2 w-full">
                  <input
                    placeholder="0"
                    type="datetime-local"
                    defaultValue={(() => {
                      const now = new Date();

                      const pad = (n) =>
                        n.toString().padStart(2, "0");

                      const year = now.getFullYear();
                      const month = pad(now.getMonth() + 1);
                      const day = pad(now.getDate());
                      const hours = pad(now.getHours());
                      const minutes = pad(now.getMinutes());

                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })()}
                    className="self-stretch h-[50px] p-2 rounded-md border border-[#687073] text-sm font-normal font-inter w-full flex-grow"
                    {...register("fecha_hora", {
                      required: "Por favor, ingrese la fecha y hora.",
                      validate: (value) => {
                        const selectedDate = new Date(value);
                        const now = new Date();
                        const threeYearsAgo = new Date();

                        threeYearsAgo.setFullYear(
                          now.getFullYear() - 3
                        );

                        if (selectedDate > now) {
                          return "La fecha no puede ser superior a la actual";
                        }

                        if (selectedDate < threeYearsAgo) {
                          return `El año no puede ser menor a ${
                            threeYearsAgo.getFullYear() + 1
                          }`;
                        }

                        return true;
                      },
                    })}
                  />
                </div>
              </div>
            </div>

            {/* Firma */}
            <div className="self-stretch flex flex-col gap-2 justify-start items-start">
              <label className="text-[#3d4245] text-sm font-normal font-inter">
                Firma
              </label>

              <SignatureCanvas
                ref={firmaRef}
                penColor="blue"
                onBegin={() => clearErrors("firma")}
                canvasProps={{
                  height: 150,
                  className: "border border-black rounded-md w-full",
                }}
              />

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={limpiarFirma}
                  className="bg-gray-300 px-4 py-2 rounded-md"
                >
                  Limpiar
                </button>
              </div>
            </div>

            {/* Mensajes de error */}
            <div className="text-start">
              {Object.values(errors).map((error, index) => (
                <p className="text-red-500" key={index}>
                  {error.message}
                </p>
              ))}
            </div>

            {/* Botones */}
            <div className="self-stretch flex justify-start items-start gap-3 sm:flex-row flex-col pb-5">
              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={loading}
              >
                Egresar vehículo
              </Button>

              <Button
                type="button"
                variant="secondary"
                size="lg"
                fullWidth
                disabled={loading}
                onClick={onCloseEgresar}
                className="sm:mb-0 mb-5"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EgresarVehiculoComponent;